/* -------------------------------------------------------------------------------------------------
Restricted - Copyright (C) Siemens Healthcare GmbH/Siemens Medical Solutions USA, Inc., 2019. All rights reserved
------------------------------------------------------------------------------------------------- */

import { css, html, LitElement } from 'lit-element';
import { sharedStyles } from '../../styles';
import { dispatchAttributeChangedEvent } from '../../utils/attribute-changed-event-dispatcher.js';
import { dispatchCustomEvent } from '../../utils/custom-event-dispatcher.js';

/**
 * @prop {String} label - Defines the label of the notification that will be displayed on top of the content.
 * @prop {String} icon - Defines the icons used for the notification that will be displayed on top of the content.
 * @prop {String} type - Defines the type of the notification, which affects which icon will be shown on the header. Possible values are `error`, `confirmation` and `alert`.
 * @prop {String} message - Defines the textual message that will be shown inside the notification.
 * @prop {String} duration - Defines the duration of how long the notification will remain visible for. Possible values are `short` (2s) and `long` (5s).
 * @prop {Boolean} visible - Defines if the notification is visible or not. Clicking on the close icon will turn it to false.
 * @prop {Boolean} sticky - If added, the notification becomes persistent and can only be closed through action buttons defined in the notification item. The close icon will be hidden.
 * @prop {Boolean} flat - Defines if the notification should have flat styles or not.
 * @prop {Boolean} new - Defines if the badge should be displayed or not.
 * @prop {Boolean} inline -  Defines if the notification should have inline styles or not.
 * @slot - Takes plain text or other components as children.
 * @slot footer - Takes button as children.
 * @fires close - Dispatched when clicking on the close icon and also on autoclose
 */

export class SHNotificationItem extends LitElement {
  static get properties() {
    return {
      label: { type: String, reflect: true },
      message: { type: String, reflect: true },
      type: { type: String, reflect: true },
      visible: { type: Boolean, reflect: true },
      duration: { type: String, reflect: true },
      sticky: { type: Boolean, reflect: true },
      icon: { type: String, reflect: true },
      flat: { type: Boolean, reflect: true },
      new: { type: Boolean, reflect: true },
      inline: { type: Boolean, reflect: true },
    };
  }
  static get styles() {
    return [
      sharedStyles,
      css`
        :host {
          width: 100%;
          height: auto;
          height: -ms-fit-content;
          height: -moz-fit-content;
          background: var(--base-4);
          box-shadow: var(--shadow-overlay);
          display: flex;
          flex-shrink: 0;
          flex-direction: column;
          border-radius: 0.125rem;
          padding: 1rem;
          box-sizing: border-box;
          overflow: hidden;
          transition: var(--transition-time) all ease-in-out, 0s max-height ease-in-out,
            0s width ease-in-out, 0s padding ease-in-out, 0s margin ease-in-out;
        }
        /* flat */
        :host([flat]) {
          background: transparent;
          box-shadow: var(--shadow-flat);
        }
        :host([inline]) {
          background: rgba(var(--ui-1), var(--opacity-7));
          box-shadow: none;
          border-left: 4px solid rgba(var(--ui-1), var(--opacity-3));
          transition: none;
        }
        :host([inline][type='alert']),
        :host([inline][type='warning']) {
          border-left: 4px solid rgb(var(--functional-yellow));
        }
        :host([inline][type='confirmation']),
        :host([inline][type='success']) {
          border-left: 4px solid rgb(var(--functional-green));
        }
        :host([inline][type='error']) {
          border-left: 4px solid rgb(var(--functional-red));
        }
        :host([flat]:hover) {
          background: rgba(var(--ui-1), var(--opacity-7));
        }
        /* hidden */
        :host(:not([visible])) {
          max-height: 0rem;
          min-height: unset;
          width: 0rem;
          padding: 0;
          margin: 0 !important;
          pointer-events: none;
          transform: translateX(5rem);
          opacity: 0;
          transition: var(--transition-time) all ease-in-out,
            0s max-height ease-in-out var(--transition-time),
            0s width ease-in-out var(--transition-time),
            0s padding ease-in-out var(--transition-time),
            0s margin ease-in-out var(--transition-time);
        }
        :host(:not([visible])[inline]) {
          transition: none;
        }
        /* header */
        .header-wrapper {
          display: flex;
          margin-bottom: 1rem;
        }

        .header-wrapper > sh-icon:not([icon='cancel']) {
          margin: 0 0.5rem 0 0;
        }

        .header-wrapper > sh-text {
          overflow: hidden;
          text-overflow: ellipsis;
          line-height: 1.5rem;
          max-height: 3rem;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          word-break: break-all;
        }
        :host(:not([label])) .default {
          display: flex;
          flex-direction: row-reverse;
        }

        :host(:not([label])) .default sh-icon {
          display: block;
        }

        .default > sh-icon:not([icon='cancel']) {
          margin: 0 0.5rem 0 0;
        }

        :host(:not([new])) #close-button {
          margin-left: 0.5rem;
        }

        /* body */
        sh-text[size='body-1'] {
          line-height: 1.5rem;
        }

        /* footer */
        .footer-wrapper {
          display: flex;
          flex-direction: row-reverse;
          align-items: center;
        }
        .footer-wrapper > ::slotted(*) {
          margin-left: 0.5rem;
          margin-top: 1rem;
        }

        /* no label */
        :host(:not([label])) sh-text[body-1] {
          margin-right: 2.5rem;
          margin-top: -2.5rem;
          width: calc(100% - 2.5rem);
        }
        :host(:not([label])[type]) sh-text[body-1] {
          margin-left: 2.5rem;
          width: calc(100% - 5rem);
        }
        .badge {
          margin: 0.5rem 1rem 0.5rem 1rem;
        }
      `,
    ];
  }
  render() {
    return html`
      ${this.label
        ? html`
            <div class="header-wrapper">
              ${this.icon &&
              this.type !== 'alert' &&
              this.type !== 'confirmation' &&
              this.type !== 'error'
                ? html` <sh-icon icon="${this.icon}" id="notification-icon" size="s"></sh-icon>`
                : ''}
              ${this.type === 'error'
                ? html`<sh-icon icon="error" class="type-icon-error" size="s"></sh-icon>`
                : ''}
              ${this.type === 'alert' || this.type === 'warning'
                ? html`<sh-icon icon="warning" class="type-icon-warning" size="s"></sh-icon>`
                : ''}
              ${this.type === 'confirmation' || this.type === 'success'
                ? html`<sh-icon icon="success" class="type-icon-success" size="s"></sh-icon>`
                : ''}
              ${this.label ? html`<sh-text size="header-2" id="label">${this.label}</sh-text>` : ''}
              ${this.new ? html`<sh-badge class="badge"></sh-badge>` : ''}
              ${!this.sticky && this.label
                ? html`<sh-icon
                    button
                    icon="cancel"
                    size="s"
                    tabindex="0"
                    @click="${() => this._fireClosevent()}"
                    id="close-button"
                  ></sh-icon>`
                : ''}
            </div>
          `
        : ''}
      <div class="default">
        ${!this.sticky && !this.label
          ? html`
              <sh-icon
                button
                icon="cancel"
                size="s"
                tabindex="0"
                @click="${() => this._fireClosevent()}"
                id="close-button-2"
              ></sh-icon>
              ${this.new ? html`<sh-badge class="badge"></sh-badge>` : ''}
            `
          : ''}
        <sh-text size="body-1"> ${this.message ? this.message : html` <slot></slot> `} </sh-text>
        ${!this.label
          ? html`
              ${this.icon &&
              this.type !== 'alert' &&
              this.type !== 'confirmation' &&
              this.type !== 'error'
                ? html` <sh-icon icon="${this.icon}" id="notification-icon" size="s"></sh-icon>`
                : ''}
              ${this.type === 'error'
                ? html`<sh-icon icon="error" class="type-icon-error" size="s"></sh-icon>`
                : ''}
              ${this.type === 'alert' || this.type === 'warning'
                ? html`<sh-icon icon="warning" class="type-icon-warning" size="s"></sh-icon>`
                : ''}
              ${this.type === 'confirmation' || this.type === 'success'
                ? html`<sh-icon icon="success" class="type-icon-success" size="s"></sh-icon>`
                : ''}
            `
          : ''}
      </div>
      <div class="footer-wrapper">
        <slot name="footer"></slot>
      </div>
    `;
  }
  _fireClosevent() {
    this.visible = false;
    // fire event on closing
    dispatchCustomEvent(this, 'close');
  }
  updated(changedProperties) {
    if (changedProperties.has('visible')) {
      this._autoClose();
    }
    const listOfProperties = [
      'label',
      'icon',
      'type',
      'message',
      'visible',
      'sticky',
      'flat',
      'new',
    ];
    dispatchAttributeChangedEvent(this, changedProperties, listOfProperties);
  }
  _autoClose() {
    const shortDuration = 2000;
    const longDuration = 5000;
    if (this.duration === 'short' && this.visible) {
      this._delay = setTimeout(() => {
        this._fireClosevent();
        clearTimeout(this._delay);
      }, shortDuration);
    }
    if (this.duration === 'long' && this.visible) {
      this._delay = setTimeout(() => {
        this._fireClosevent();
        clearTimeout(this._delay);
      }, longDuration);
    }
  }
}
customElements.define('sh-notification-item', SHNotificationItem);
